import Big from 'big.js';
import { NumberConvertOptions, RadixPointType, ToRoundProps } from '../types/common';
import { OrderSide, OrderUnitType } from '../types/trade';
import { VaildNumber } from './format';
import { getPrecision } from './helper';

Big.NE = -19;

function toRound({ int, decimal, precision, intLen = 0, decimalLen = 0 }: ToRoundProps) {
  const safeDecimalLen = 8,
    safeIntLen = 15;
  intLen = intLen || int.length;
  decimalLen = decimalLen || decimal.length;
  let i = 1,
    dCarryFlag = false,
    returnDecimal = '';
  while (i && i <= Math.ceil(decimalLen / safeDecimalLen)) {
    const newDecimal = decimal.slice(-i * safeDecimalLen, (1 - i) * safeDecimalLen || precision + 1);
    const newDecimalLen = newDecimal.length;
    let status: 'INCREASE' | 'DAP' = 'INCREASE';

    if (i === 1) {
      const temp = Number(`0.${newDecimal}`).toFixed(Math.min(precision, safeDecimalLen - 1));
      if (temp.startsWith('1')) {
        returnDecimal = '0'.repeat(newDecimalLen - 1);
      } else {
        returnDecimal = decimal.slice(0, -safeDecimalLen) + temp.slice(2);
        status = 'DAP';
      }
    } else {
      const temp = (Number(newDecimal) + 1).toString();
      if (temp.length > newDecimalLen) {
        returnDecimal = '0'.repeat(newDecimalLen) + returnDecimal;
      } else {
        returnDecimal = decimal.slice(0, -i * safeDecimalLen) + temp + returnDecimal;
        status = 'DAP';
      }
    }

    if (status === 'INCREASE') {
      i++;
      dCarryFlag = true;
    } else {
      i = 0;
      dCarryFlag = false;
    }
  }
  i = 1; // reset

  let iCarryFlag = false,
    returnInt = '';
  if (dCarryFlag) {
    while (i && i <= Math.ceil(intLen / safeIntLen)) {
      const newInt = int.slice(-i * safeIntLen, (1 - i) * safeIntLen || intLen);
      const newIntLen = newInt.length;
      const temp = (Number(newInt) + 1).toString();
      if (temp.length > newIntLen) {
        i++;
        iCarryFlag = true;
        returnInt = '0'.repeat(newIntLen) + returnInt;
      } else {
        returnInt = int.slice(0, -i * safeIntLen) + temp + returnInt;
        i = 0;
        iCarryFlag = false;
      }
    }
  }

  return [iCarryFlag ? `1${'0'.repeat(int.length)}` : returnInt || int, returnDecimal];
}

function toCeil({ int, decimal, precision }: ToRoundProps) {
  let carryFlag = false;
  let returnDecimal = decimal.slice(0, precision);

  if (decimal.length > precision) {
    let nextDigitIndex = precision;
    let nextDigit = Number(decimal[nextDigitIndex] || '0');

    while (nextDigit === 0 && nextDigitIndex < decimal.length - 1) {
      nextDigitIndex++;
      nextDigit = Number(decimal[nextDigitIndex] || '0');
    }

    if (nextDigit > 0) {
      const temp = (Number(`0.${returnDecimal}`) + Math.pow(10, -precision)).toFixed(precision);
      returnDecimal = temp.slice(2);
      carryFlag = temp.startsWith('1');
    }
  }

  let returnInt = int;
  if (carryFlag) {
    returnInt = (Number(int) + 1).toString();
  }

  return [returnInt, returnDecimal];
}

function toPrecision(str: string, precision: number, type: RadixPointType, originStringLen?: number) {
  if (typeof str !== 'string' || precision <= 0) return '';
  let len = originStringLen ?? str.length;

  switch (type) {
    case 'NORMAL':
      return len === 0 ? '' : '.' + str.slice(0, precision);
    case 'SUPPLY_ZERO':
      return '.' + (len < precision ? str.padEnd(precision, '0') : str.slice(0, precision));
    default: {
      let i = 1;
      len = Math.min(len, precision);
      str = str.slice(0, len);
      while (i && i <= Math.ceil(len / 16)) {
        const temp = Number(`0.${str.slice(-16)}`);
        if (temp === 0) {
          str = str.slice(0, -16);
          i++;
        } else {
          str = str.slice(0, -16) + toNonExponential(temp).slice(2);
          i = 0;
        }
      }
      return str ? `.${str}` : '';
    }
  }
}

export function toNonExponential(num: number): string {
  const m = num.toExponential().match(/\d(?:\.(\d*))?e([+-]\d+)/);
  return num.toFixed(Math.max(0, (m?.[1] || '').length - Number(m?.[2] || 0)));
}

export function toFraction(str = '', place = 3, originStringLen?: number): string {
  const reg = new RegExp(`(\\d)(?=(?:\\d{${place}})+$)`, 'g');
  return (originStringLen ?? str.length) > place ? str.replace(reg, '$1,') : str;
}

export function toConvertNumber(str: string, options?: NumberConvertOptions): string {
  const { precision = 8, separator = true, round = false, ceil = false } = options || {};
  let radixPoint = options?.radixPoint || 'NORMAL';
  if (typeof str !== 'string' || !str.length || Number.isNaN(Number(str))) return '';
  let [int = '0', decimal = ''] = str.split('.');
  let sign = int.slice(0, 1); // for + -
  ['+', '-'].includes(sign) ? (int = int.slice(1)) : (sign = '');

  if (precision < 0) {
    const l = int.length - Math.abs(precision);
    const processedInt = int === '0' ? int : int.slice(0, l) + '0'.repeat(Math.abs(precision));

    // 添加千分位处理
    const newInt = separator ? toFraction(processedInt, undefined, processedInt.length) : processedInt;
    return Number(newInt) === 0 || sign !== '-' ? newInt : sign + newInt;
  }
  const decimalLen = decimal.length;
  int = Number(int) === 0 ? '0' : int;
  const intLen = int.length;
  if (precision < decimalLen) {
    if (round) {
      decimal = decimal.slice(0, precision + 1);
      radixPoint = 'NORMAL';
      [int, decimal] = toRound({ int, decimal, precision, intLen, decimalLen: decimal.length });
    } else if (ceil) {
      // decimal = decimal.slice(0, precision + 1);
      radixPoint = 'NORMAL';
      [int, decimal] = toCeil({ int, decimal, precision });
    }
  }
  const newInt = separator ? toFraction(int, undefined, intLen) : int;
  const newDecimal = toPrecision(
    decimal,
    precision,
    round && precision >= decimalLen ? 'SUPPLY_ZERO' : radixPoint,
    decimalLen
  );
  const r = newInt + newDecimal;
  return Number(r) === 0 || sign !== '-' ? r : sign + r;
}

export const calculateQuantity = (
  price: string,
  amount: string,
  contract: string,
  unit: OrderUnitType,
  multiplier: string
): string => {
  if (amount === '' || amount === '0' || Number(amount) === 0) {
    return '0';
  }

  const quantityFormula = {
    '0': () => {
      const value = new Big(VaildNumber(amount)).div(VaildNumber(contract, 1)).toString();
      return toConvertNumber(value, { precision: getPrecision(multiplier), separator: false });
    },
    '1': () => {
      return amount;
    },
    '2': () => {
      if (VaildNumber(price) <= 0) {
        return '0';
      } else {
        if (
          new Big(multiplier)
            .minus(new Big(VaildNumber(amount)).div(VaildNumber(price, 1)).div(VaildNumber(contract, 1)))
            .abs()
            .lt(new Big(0.2).times(multiplier))
        ) {
          return multiplier;
        } else {
          const count = new Big(VaildNumber(amount))
            .div(VaildNumber(price, 1))
            .div(VaildNumber(contract, 1))
            .toString();
          return toConvertNumber(count, { precision: getPrecision(multiplier), separator: false });
        }
      }
    },
    '3': () => {
      //TODO 占位解决类型错误
      return '';
    },
    '4': () => {
      return '0';
    },
  };
  return quantityFormula[unit]();
};

export const calculatePercentageQuantity = (
  maxBuy: string,
  maxSell: string,
  percentage: number,
  side: OrderSide,
  multiplier: number
): string => {
  const quantityFormula = {
    BUY: () => {
      try {
        return toConvertNumber(new Big(VaildNumber(maxBuy)).times(VaildNumber(percentage)).div(100).toString(), {
          precision: multiplier,
          separator: false,
        });
      } catch (error) {
        console.log(error);
      }
      return '0';
    },
    SELL: () => {
      try {
        return toConvertNumber(new Big(VaildNumber(maxSell)).times(VaildNumber(percentage)).div(100).toString(), {
          precision: multiplier,
          separator: false,
        });
      } catch (error) {
        console.log(error);
      }
      return '0';
    },
  };
  return side === 'BUY' || side === 'buy' ? quantityFormula.BUY() : quantityFormula.SELL();
};

/**
 * 计算等比数列的前 n 项和 (使用 Big.js 进行精确计算)
 *
 * 使用公式：
 * - 当 r = 1 时: S_n = a * n
 * - 当 r ≠ 1 时: S_n = a * (r^n - 1) / (r - 1)
 *
 * @param a - 首项 (first term, Big.js instance)
 * @param r - 公比 (common ratio, Big.js instance)
 * @param n - 项数 (number of terms, Big.js instance)
 * @returns 等比数列的前 n 项和 (Big.js instance)
 * @throws Error 如果项数 n 小于 0
 *
 * @example
 * const a = new Big(1);
 * const r = new Big(2);
 * const n = new Big(5);
 * const sum = calculateGeometricSeriesSum(a, r, n); // 返回 Big(31) (1 + 2 + 4 + 8 + 16)
 */
export function calculateGeometricSeriesSum(a: Big, r: Big, n: Big): Big {
  // 验证 n 必须为非负数
  if (n.lt(0)) {
    throw new Error('Number of terms (n) must be non-negative.');
  }

  // 如果项数为 0，和为 0
  if (n.eq(0)) {
    return new Big(0);
  }

  // 情况一：公比 r 等于 1
  if (r.eq(1)) {
    return a.times(n);
  }

  // 情况二：公比 r 不等于 1
  // 使用 Big.pow() 计算 r 的 n 次幂
  // S_n = a * (r^n - 1) / (r - 1)
  return a.times(r.pow(n.toNumber()).minus(1)).div(r.minus(1));
}

/**
 * 使用二分法查找等比数列和小于目标值的最大项数
 *
 * 该函数用于快速查找满足 S_n < target 的最大项数 n，其中 S_n 为等比数列的前 n 项和。
 * 时间复杂度：O(log n)，相比线性查找 O(n) 有显著性能提升。
 *
 * @param a - 等比数列首项 (Big.js instance)
 * @param r - 等比数列公比 (Big.js instance)
 * @param target - 目标值，查找和小于该值的最大项数 (Big.js instance)
 * @param maxN - 最大项数限制，搜索范围的上界
 * @returns 满足条件的最大项数（从 1 开始计数），如果第 1 项就已经 >= target，则返回 0
 *
 * @example
 * // 查找等比数列 0.05, 0.06, 0.072, ... (a=0.05, r=1.2) 累加和小于 1 的最大项数
 * const a = new Big('0.05');
 * const r = new Big('1.2');
 * const target = new Big(1);
 * const maxN = 100;
 * const result = findMaxTermsBelowTarget(a, r, target, maxN);
 * // result = 项数 n，使得 S_n < 1 但 S_(n+1) >= 1
 */
export function findMaxTermsBelowTarget(a: Big, r: Big, target: Big, maxN: number): number {
  // 边界情况：如果 maxN < 1，直接返回 0
  if (maxN < 1) {
    return 0;
  }

  // 检查第 1 项是否已经 >= target
  const firstTermSum = calculateGeometricSeriesSum(a, r, new Big(1));
  if (firstTermSum.gte(target)) {
    return 0;
  }

  // 检查 maxN 项的和是否 < target，如果是则直接返回 maxN
  const maxSum = calculateGeometricSeriesSum(a, r, new Big(maxN));
  if (maxSum.lt(target)) {
    return maxN;
  }

  // 二分查找
  let left = 1;
  let right = maxN;
  let result = 0;

  while (left <= right) {
    const mid = Math.floor((left + right) / 2);
    const sum = calculateGeometricSeriesSum(a, r, new Big(mid));

    if (sum.lt(target)) {
      // 当前项数的和小于目标值，记录结果并尝试更大的项数
      result = mid;
      left = mid + 1;
    } else {
      // 当前项数的和 >= 目标值，尝试更小的项数
      right = mid - 1;
    }
  }

  return result;
}
